// noinspection JSUnusedGlobalSymbols,JSUnresolvedReference

export class ShoppingCart {
    constructor({id, store, client, status, items}) {
        this.id = id;
        this.store = store;
        this.client = client;
        this.status = status || 'updated';
        this.items = items || [];
    };

    addItem(item) {
        const matchIndex = this.items.findIndex(i => i.id === item.id);
        if (matchIndex >= 0) this.items.increaseQuantity();
        else this.items.push(item);
    };

    deleteItem(id) {
        this.items = this.items.filter(item => item.id !== id);
    };

    getItemById(id) {
        return this.items.find(item => item.id === id);
    };

    getItemByProduct(product) {
        return this.items.find(item => item.product === product);
    };

    markItemAsActive(id) {
        const matchIndex = this.items.findIndex(item => item.id === id);
        if (matchIndex >= 0) this.items[matchIndex].markAsActive();
    };

    markItemAsDeleted(id) {
        const matchIndex = this.items.findIndex(item => item.id === id);
        if (matchIndex >= 0) this.items[matchIndex].markAsDeleted();
    };

    hasItems() {
        return this.items && this.items.length > 0;
    };

    clear() {
        this.items = [];
    };

    getTotalQuantity() {
        let quantity = 0;
        for (let i = 0; i < this.items.length; i++) {
            const item = this.items[i];
            const itemQuantity = item.quantity || 0;
            quantity += itemQuantity;
        }
        return quantity;
    };

    getTotal(taxMultiplier) {
        let total = 0;
        for (let i = 0; i < this.items.length; i++) {
            const item = this.items[i];
            const itemTotal = (item.offerPrice || item.normalPrice) && item.quantity ? (item.offerPrice || item.normalPrice) * item.quantity : undefined;
            if (itemTotal) total += itemTotal;
            else {
                total = undefined;
                break;
            }
        }
        if (!total) return undefined;
        else return taxMultiplier !== undefined && taxMultiplier !== null && taxMultiplier > 0 ? Math.round(total * taxMultiplier) : total;
    };

    getExclusiveTotal() {
        let total = 0;
        for (let i = 0; i < this.items.length; i++) {
            const item = this.items[i];
            const itemTotal = (item.exclusivePrice || item.offerPrice || item.normalPrice) && item.quantity ? (item.exclusivePrice || item.offerPrice || item.normalPrice) * item.quantity : undefined;
            if (itemTotal) total += itemTotal;
            else {
                total = undefined;
                break;
            }
        }
        return total;
    };

    getMaxPriceItem() {
        let maxPrice = 0;
        let maxItem = undefined;
        this.items.forEach(item => {
            const price = item.exclusivePrice || item.offerPrice || item.normalPrice;
            if (price > maxPrice) {
                maxPrice = price;
                maxItem = item;
            }
        });
        return maxItem;
    };

    hasQueryIds = () => {
        return this.items.some(item => !!item.queryId);
    };

    listProductsIds = () => {
        return this.items.map((item) => {
            if (item.type === 'variant') return item.parent;
            else return item.product;
        });
    };

    listProductsAndVariantsIds = () => {
        return this.items.map((item) => ({id: item.product, parent: item.parent, type: item.type}));
    };

    static fromStorage(data) {
        const shoppingCart = new ShoppingCart({id: data.id, store: data.store, client: data.client, items: []});
        data.items.forEach(item => shoppingCart.addItem(ShoppingCartItem.fromStorage(item)));
        return shoppingCart;
    };

    toStorage() {
        return {
            id: this.id,
            client: this.client,
            store: this.store,
            items: this.items.map(item => item.toStorage())
        };
    };

    toActivityLog(customQuantity) {
        return {
            id: this.id,
            store: this.store,
            client: this.client,
            status: this.status,
            items: this.items.map((item) => item.toActivityLog(customQuantity && customQuantity.id === item.id ? customQuantity.quantity : item.quantity))
        };
    };

    toCheckout() {
        return {
            details: this.items.filter((item) => (item.quantity > 0 && item.status === "active")).map(item => item.toCheckout())
        };
    };

    clone() {
        return new ShoppingCart({
            id: this.id,
            store: this.store,
            client: this.client,
            status: this.status,
            items: this.items.map(item => item.clone())
        });
    };
}

export class ShoppingCartItem {
    constructor({id, sku, product, type, parent, name, quantity, minQuantity, maxQuantity, normalPrice, offerPrice, exclusivePrice, status, queryId, additionalData}) {
        this.id = id;
        this.sku = sku;
        this.product = product;
        this.type = type;
        this.parent = parent;
        this.name = name;
        this.quantity = quantity;
        this.minQuantity = minQuantity;
        this.maxQuantity = maxQuantity;
        this.normalPrice = normalPrice;
        this.offerPrice = offerPrice;
        this.exclusivePrice = exclusivePrice;
        this.status = status || 'active';
        this.queryId = queryId;
        this.additionalData = additionalData;
    };

    setQuantity(quantity) {
        if (quantity <= this.maxQuantity && quantity >= this.minQuantity) this.quantity = quantity;
    };

    increaseQuantity() {
        if (this.quantity < this.maxQuantity) this.quantity += 1;
    };

    decreaseQuantity() {
        if (this.quantity > this.minQuantity) this.quantity -= 1;
    };

    minPrice() {
        return this.exclusivePrice || this.offerPrice || this.normalPrice;
    };

    getOfferDiscount() {
        return this.offerPrice ? this.normalPrice - this.offerPrice : 0;
    };

    markAsActive() {
        this.status = 'active';
    };

    markAsDeleted() {
        this.status = 'deleted';
    };

    toStorage() {
        return {
            id: this.id,
            sku: this.sku,
            product: this.product,
            type: this.type,
            parent: this.parent,
            name: this.name,
            quantity: this.quantity,
            minQuantity: this.minQuantity,
            maxQuantity: this.maxQuantity,
            normalPrice: this.normalPrice,
            offerPrice: this.offerPrice,
            exclusivePrice: this.exclusivePrice,
            status: this.status,
            queryId: this.queryId,
            additionalData: this.additionalData
        };
    };

    toActivityLog(quantity) {
        return { // Do not include additionalData in the activity log, it's not needed.
            id: this.id,
            sku: this.sku || null,
            product: this.product,
            type: this.type,
            parent: this.parent || null,
            name: this.name || null,
            quantity: quantity >= 0 ? quantity : this.quantity,
            min_quantity: this.minQuantity !== undefined ? this.minQuantity : null,
            max_quantity: this.maxQuantity !== undefined ? this.maxQuantity : null,
            normal_price: this.normalPrice !== undefined ? this.normalPrice : null,
            offer_price: this.offerPrice !== undefined ? this.offerPrice : null,
            exclusive_price: this.exclusivePrice !== undefined ? this.exclusivePrice : null,
            status: this.status,
            query_id: this.queryId || null
        };
    };

    toCheckout() {
        return {
            id: this.parent ? this.parent : this.product,
            variant: this.parent ? this.product : null,
            quantity: this.quantity
        };
    };

    toExternalMetric() {
        return {
            name: this.name ? this.name.toLowerCase() : undefined,
            id: this.product ? this.product.toLowerCase() : undefined,
            normalPrice: this.normalPrice || this.offerPrice || this.exclusivePrice,
            reducedPrice: this.exclusivePrice || this.offerPrice || this.normalPrice,
            brand: this.additionalData && this.additionalData.brand ? this.additionalData.brand : undefined,
            type: this.additionalData && this.additionalData.type ? this.additionalData.type : undefined,
            segment: this.additionalData && this.additionalData.segment ? this.additionalData.segment : undefined,
            model: this.additionalData && this.additionalData.model ? this.additionalData.model : undefined,
            memory: this.additionalData && this.additionalData.memory ? this.additionalData.memory : undefined,
            subtype: this.additionalData && this.additionalData.subtype ? this.additionalData.subtype : undefined,
            color: this.additionalData && this.additionalData.color ? this.additionalData.color : undefined
        };
    };

    static fromStorage(data) {
        return new ShoppingCartItem({
            id: data.id,
            sku: data.sku,
            product: data.product,
            type: data.type,
            parent: data.parent,
            name: data.name,
            quantity: data.quantity,
            minQuantity: data.minQuantity,
            maxQuantity: data.maxQuantity,
            normalPrice: data.normalPrice,
            offerPrice: data.offerPrice,
            exclusivePrice: data.exclusivePrice,
            status: data.status,
            queryId: data.queryId,
            additionalData: data.additionalData
        });
    };

    clone() {
        return new ShoppingCartItem({
            id: this.id,
            sku: this.sku,
            product: this.product,
            type: this.type,
            parent: this.parent,
            name: this.name,
            quantity: this.quantity,
            minQuantity: this.minQuantity,
            maxQuantity: this.maxQuantity,
            normalPrice: this.normalPrice,
            offerPrice: this.offerPrice,
            exclusivePrice: this.exclusivePrice,
            status: this.status,
            queryId: this.queryId,
            additionalData: this.additionalData
        });
    };
}